import { useEffect, useState, type ReactNode } from "react";
import { useLocation, type Location } from "react-router";

function routeGroup(pathname: string) {
  if (pathname.startsWith("/moodboard")) return "/moodboard";
  if (pathname.startsWith("/share")) return "/share";
  if (pathname === "/login" || pathname === "/register") return pathname;
  if (pathname.startsWith("/community")) return "/community";
  if (pathname.startsWith("/site")) return "/site";
  if (pathname === "/" || pathname === "/library" || pathname === "/workbench") return pathname;
  return "/";
}

function reducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function RouteTransition({ children }: { children: (location: Location) => ReactNode }) {
  const location = useLocation();
  const [rendered, setRendered] = useState(location);
  const [phase, setPhase] = useState<"idle" | "in" | "out">("idle");

  useEffect(() => {
    if (routeGroup(location.pathname) === routeGroup(rendered.pathname)) {
      setRendered(location);
      return;
    }
    if (reducedMotion()) {
      setRendered(location);
      setPhase("idle");
      return;
    }
    setPhase("out");
    const leave = window.setTimeout(() => {
      setRendered(location);
      setPhase("in");
    }, 160);
    return () => window.clearTimeout(leave);
  }, [location, rendered.pathname]);

  useEffect(() => {
    if (phase !== "in") return;
    const settle = window.setTimeout(() => setPhase("idle"), 420);
    return () => window.clearTimeout(settle);
  }, [phase]);

  return (
    <div className={`route-transition is-${phase}`} data-route={routeGroup(rendered.pathname)}>
      {children(rendered)}
    </div>
  );
}
